import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import {apiUrls} from "../utils/utils.ts";
import {
    CtrAddenda,
    CtrCaracteristiques,
    CtrDepenses,
    CtrDynamicTables,
    CtrInscriptions,
    CtrLiensAdditionnels,
    CtrPhotos,
    CtrPiecesUnites,
    CtrRemarques,
    CtrRenovations,
    CtrUnitesDetaillees,
    CtrUnitesSommaires,
    CtrVisitesLibres
} from "../graphql/generated/graphql-main.tsx";
import {cfg} from "../config.ts";

const {rest_paths} = cfg.api;

const ctrDynTable2Url
    = (tbl: CtrDynamicTables, param: string) => `${rest_paths.root_mls}/${tbl.toLowerCase()}/${param}`;

export const restApi = createApi({
    reducerPath: "api_rest",
    baseQuery: fetchBaseQuery({baseUrl: apiUrls.rest()}),
    endpoints: (builder) => ({
        // get JSON object of official Engel & Voelkers offices w/ their custom label
        fetchEvOffices: builder.query<{ [code: string]: string }, void>({
            query: () => `${rest_paths.root_core}/${rest_paths.core_ev_offices}`
        }),

        // fetch single MLS listing (160+ fields) given a single MLS ID
        fetchListing: builder.query<CtrInscriptions, string>({
            query: (mlsId) => ctrDynTable2Url(CtrDynamicTables.Inscriptions, mlsId)
        }),
        fetchCharacteristics: builder.query<CtrCaracteristiques[], string>({
            query: (mlsId) => ctrDynTable2Url(CtrDynamicTables.Caracteristiques, mlsId)
        }),
        fetchRooms: builder.query<CtrPiecesUnites[], string>({
            query: (mlsId) => ctrDynTable2Url(CtrDynamicTables.PiecesUnites, mlsId)
        }),
        fetchPhotos: builder.query<CtrPhotos[], string>({
            query: (mlsId) => ctrDynTable2Url(CtrDynamicTables.Photos, mlsId)
        }),
        fetchAddenda: builder.query<CtrAddenda[], string>({
            query: (mlsId) => ctrDynTable2Url(CtrDynamicTables.Addenda, mlsId)
        }),
        fetchExpenditures: builder.query<CtrDepenses[], string>({
            query: (mlsId) => ctrDynTable2Url(CtrDynamicTables.Depenses, mlsId)
        }),
        fetchAdditionalLinks: builder.query<CtrLiensAdditionnels[], string>({
            query: (mlsId) => ctrDynTable2Url(CtrDynamicTables.LiensAdditionnels, mlsId)
        }),
        fetchRemarks: builder.query<CtrRemarques[], string>({
            query: (mlsId) => ctrDynTable2Url(CtrDynamicTables.Remarques, mlsId)
        }),
        fetchRenovations: builder.query<CtrRenovations[], string>({
            query: (mlsId) => ctrDynTable2Url(CtrDynamicTables.Renovations, mlsId)
        }),
        fetchDetailedUnits: builder.query<CtrUnitesDetaillees[], string>({
            query: (mlsId) => ctrDynTable2Url(CtrDynamicTables.UnitesDetaillees, mlsId)
        }),
        fetchSummaryUnits: builder.query<CtrUnitesSommaires[], string>({
            query: (mlsId) => ctrDynTable2Url(CtrDynamicTables.UnitesSommaires, mlsId)
        }),
        fetchOpenVisits: builder.query<CtrVisitesLibres[], string>({
            query: (mlsId) => ctrDynTable2Url(CtrDynamicTables.VisitesLibres, mlsId)
        }),
    })
});

export const {
    useLazyFetchEvOfficesQuery,
    useFetchListingQuery,
    useFetchCharacteristicsQuery,
    useFetchRoomsQuery,
    useFetchPhotosQuery,
    useFetchAddendaQuery,
    useFetchExpendituresQuery,
    useFetchAdditionalLinksQuery,
    useFetchRemarksQuery,
    useFetchRenovationsQuery,
    useFetchDetailedUnitsQuery,
    useFetchSummaryUnitsQuery,
    useFetchOpenVisitsQuery
} = restApi;
